import React, { useState, useEffect, useRef, useMemo } from "react";
import api from "../Utils/api";
import { toast } from "react-toastify";
import { MoreVertical } from "lucide-react";
import UserAvatar from "../Components/User/UserAvatar";
import UserProfileDialog from "../Components/User/UserProfileDialog";
import UserAddDialog from "../Components/User/AddUserModal";
import DropDownMenu from "../Components/User/Dropdown";
import { Search } from "lucide-react";
import EmploymentDetails from "../Components/User/EmploymentDetails";
import UserMenu from "../Components/User/UserMenu";
import PersonalDetailsForm from "../Components/User/PersonalDetails";
import FilterPopover from "../Components/User/FilterPopover";
import DisplayDropdown from "../Components/User/DislpayButton";
import { accessMap, employmentTypes, stressProfileMap } from "../Utils/const";
import { setUser } from "../Store/Actions/userActions";
import { useSelector } from "react-redux";
import Spinner from "../Components/Spinner";

const People = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [dropdownIndex, setDropdownIndex] = useState(null);
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [modify, setModify] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // const [currentPage, setCurrentPage] = useState(1);

  const [selectedUserID, setSelectedUserID] = useState("");

  const [openEmployment, setOpenEmployment] = useState(false);
  const [openPersonal, setOpenPersonal] = useState(false);
  const [openView, setOpenView] = useState(false);

  const [locationManagerLocations, setLocationManagerLocations] = useState([]);
  const [isLocationManger, setIsLocationManager] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const user = useSelector((state) => state.user).user;

  useEffect(() => {
    if (user) {
      if (user.deputyAccessLevel === "location_manager") {
        let locations = [];
        locations.push(user.locations.primaryLocation._id);
        locations.push(...user.locations.subLocations.map((item) => item._id));
        setLocationManagerLocations(locations);
        setIsLocationManager(true);
        // console.log(locations);
      }
    }
  }, [user]);

  const [displayedFields, setDisplayedFields] = useState([
    "Main Location",
    "Status",
    "Email",
    "Mobile",
    "Stress Profile",
    "Pay Rates",
  ]);

  const [filters, setFilters] = useState({
    status: "All",
    location: "All",
    payrollId: "All",
    leaveTypes: [],
    access: "all",
    stress: "all",
  });

  const [tableDisplay, setTableDisplay] = useState({
    main_location: true,
    status: true,
    email: true,
    mobile: true,
    stress_profile: true,
    training: false,
    payrates: true,
    payrollId: false,
    leave_balance: false,
    connected_to: false,
  });

  const StatusMap = {
    all: true,
    employed: true,
    archived: false,
  };

  useEffect(() => {
    if (users.length > 0 && isLocationManger) {
      setUsers(
        users.filter((user) => {
          // Safely get primary location _id (could be null)
          const primaryId = user.locations.primaryLocation?._id;
          // Get all subLocation _ids
          const subLocationIds = (user.locations.subLocations || []).map(
            (sub) => sub._id
          );
          // Combine into one array, filter out null/undefined
          const allLocationIds = [primaryId, ...subLocationIds].filter(Boolean);

          // Check if any of these IDs are in locationManagerLocations
          return allLocationIds.some((id) =>
            locationManagerLocations.includes(id)
          );
        })
      );
    }
  }, [users]);

  useEffect(() => {
    const newTableDisplay = {};
    displayedFields.forEach((label) => {
      const key = labelToKey(label);
      newTableDisplay[key] = displayedFields.includes(label);
    });

    setTableDisplay(newTableDisplay);
  }, [displayedFields]);

  useEffect(() => {
    // Filter logic whenever filters change
    const filtered = users.filter((person) => {
      // Status filter
      const matchStatus =
        filters.status === "All" ||
        person.allowed === StatusMap[filters.status];

      // Location filter
      const matchLocation =
        filters.location === "All" ||
        person.locations?.primaryLocation?._id === filters.location;

      // Payroll ID filter
      const matchPayrollId =
        filters.payrollId === "all"
          ? true
          : filters.payrollId === "yes"
          ? !!person.payrollId
          : filters.payrollId === "no"
          ? !person.payrollId
          : true; // fallback, in case of unexpected value

      // Stress Profile filter
      const matchStressProfile =
        filters.stress === "all" || person.stressProfile === filters.stress;

      // // Access filter
      const matchAccess =
        !filters.access ||
        filters.access === "all" ||
        person.deputyAccessLevel === filters.access;

      // Leave Types filter (array)
      const matchLeaveType =
        !filters.leaveTypes ||
        filters.leaveTypes.length === 0 ||
        filters.leaveTypes.some((type) =>
          person.leaveEntitlements.some((item) => item.id == type.id)
        );

      // Combine all filters
      return (
        matchStatus &&
        matchLocation &&
        matchPayrollId &&
        matchStressProfile &&
        matchAccess &&
        matchLeaveType
      );
    });

    setFilteredPeoples(filtered);
  }, [filters, users]);

  const showPersonalInfoEdit = (id) => {
    setSelectedUserID(id);
    setOpenPersonal(true);
  };

  const showUserView = (id) => {
    setSelectedUserID(id);
    setOpenView(true);
  };

  const showEmploymentInfoEdit = (id) => {
    setSelectedUserID(id);
    setOpenEmployment(true);
  };

  const dropdownRefs = useRef({});

  useEffect(() => {
    getUsers();
    getLocations();
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        dropdownIndex !== null &&
        dropdownRefs.current[dropdownIndex] &&
        !dropdownRefs.current[dropdownIndex].contains(event.target)
      ) {
        setDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [dropdownIndex]);

  const handleModifyUser = (index, id) => {
    setModify(true);
    setSelectedUserID(id);
  };

  const labelToKey = (label) => {
    // Map label to your tableDisplay key format
    switch (label) {
      case "Main Location":
        return "main_location";
      case "Status":
        return "status";
      case "Email":
        return "email";
      case "Mobile":
        return "mobile";
      case "Stress Profile":
        return "stress_profile";
      case "Training":
        return "training";
      case "Pay Rates":
        return "payrates";
      case "Payroll ID":
        return "payrollId";
      case "Leave Balance":
        return "leave_balance";
      case "Connected to":
        return "connected_to";
      default:
        return label.toLowerCase().replace(/\s+/g, "_");
    }
  };

  const getUsers = async () => {
    try {
      const response = await api.get("/user");
      setUsers(response.data.users.filter((u) => !u.archive));
      setFilteredPeoples(response.data.users);
      setCurrentPage(1); // reset page on new data
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch users");
    }
  };

  const closeEmploymentDetails = () => {
    getUsers();
    setOpenEmployment(false);
  };

  const closePersonalDetails = () => {
    getUsers();
    setOpenPersonal(false);
  };

  const toggleUserAllowBlock = async (userID, allow) => {
    if (allow == undefined || allow == null) {
      allow = false;
    }
    const res = await api.post("/user/update", {
      _id: userID,
      updatedData: {
        allowed: !allow,
      },
    });

    if (res.data.success) {
      toast.success(res.data.message);
      getUsers();
    } else {
      toast.error(res.data.message);
    }
  };

  const getLocations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get("/location");
      setIsLoading(false);
      setLocations(response.data.locations.filter((loc) => !loc.archive));
    } catch (error) {
      console.error(error);
      toast.error("Failed to fetch locations");
    }
  };

  const archiveUser = async (userID) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;

    try {
      const res = await api.post(`/user/archive`, {
        userID: userID,
      });
      if (res.data.success) {
        toast.success(res.data.message);
        getUsers();
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to delete user");
      console.error(error);
    }
  };

  const getLocationName = (locationID) => {
    const loc = locations.find((l) => l._id === locationID);
    return loc ? loc.name : "N/A";
  };

  const [filteredPeoples, setFilteredPeoples] = useState(users);

  const handlePeopleSearch = () => {
    if (searchTerm !== "") {
      const filteredPeopleData = users.filter((user) => {
        const search = searchTerm.toLowerCase();
        return (
          (user.firstName && user.firstName.toLowerCase().includes(search)) ||
          (user.lastName && user.lastName.toLowerCase().includes(search))
        );
      });
      setFilteredPeoples(filteredPeopleData);
    } else {
      const filtered = users.filter((person) => {
        // Status filter
        const matchStatus =
          filters.status === "All" ||
          person.allowed === StatusMap[filters.status];

        // Location filter
        const matchLocation =
          filters.location === "All" ||
          person.locations?.primaryLocation?._id === filters.location;

        // Payroll ID filter
        const matchPayrollId =
          filters.payrollId === "all"
            ? true
            : filters.payrollId === "yes"
            ? !!person.payrollId
            : filters.payrollId === "no"
            ? !person.payrollId
            : true; // fallback, in case of unexpected value

        // Stress Profile filter
        const matchStressProfile =
          filters.stress === "all" || person.stressProfile === filters.stress;

        // // Access filter
        const matchAccess =
          !filters.access ||
          filters.access === "all" ||
          person.deputyAccessLevel === filters.access;

        // Leave Types filter (array)
        const matchLeaveType =
          !filters.leaveTypes ||
          filters.leaveTypes.length === 0 ||
          filters.leaveTypes.some((type) =>
            person.leaveEntitlements.some((item) => item.id == type.id)
          );

        // Combine all filters
        return (
          matchStatus &&
          matchLocation &&
          matchPayrollId &&
          matchStressProfile &&
          matchAccess &&
          matchLeaveType
        );
      });

      setFilteredPeoples(filtered);
    }
  };

  const handlePeopleKeyDown = (e) => {
    if (e.key === "Enter") {
      handlePeopleSearch();
    }
  };

  //-------------pagination--------------------
  const [currentPage, setCurrentPage] = useState(1);

  const [rowsPerPage, setRowsPerPage] = useState(30);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredPeoples.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(users.length / rowsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const actionOptions = ["View", "Edit Personal", "Edit Employment"];

  const handleSelectAction = (value) => {};

  const AddModalClose = () => {
    setOpenAddUserModal(false);
    getUsers();
  };

  return (
    <div className="px-6 mt-20 mx-10 pb-20">
      {isLoading && <Spinner />}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-600">People</h1>
        <button
          onClick={() => setOpenAddUserModal(true)}
          className="bg-gray-500 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-600 transition"
        >
          + Add People
        </button>
      </div>

      <div className="pt-2">
        <div className="min-w-full !min-h-[55px] flex justify-between items-start pl-2">
          <ul className="w-full flex items-center space-x-2 font-[sans-serif] mt-2">
            <li
              className={`w-[300px] text-gray-500 bg-[transparent] text-sm tracking-wide flex items-center rounded cursor-pointer hover:!border-[#265c7f] bg-[transparent]`}
            >
              <div className="w-full relative">
                <input
                  type="text"
                  placeholder="Search"
                  className="!w-full border border-gray-500 rounded-md py-1.5 px-4 pl-10 focus:outline-none focus:ring-1 focus:ring-gray-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={handlePeopleKeyDown}
                />
                <Search
                  className="absolute left-3 top-2.5 text-gray-500"
                  size={16}
                />
              </div>
            </li>
            <li
              className={` bg-gray-500 px-4 py-2 text-sm tracking-wide flex items-center rounded hover:bg-gray-700 cursor-pointer hover:!border-[#265c7f]`}
              onClick={() => handlePeopleSearch()}
            >
              <span className="text-[white]">Search</span>
            </li>
            <li>
              <FilterPopover
                locations={locations.map((loc) => {
                  return {
                    name: loc.name,
                    id: loc._id,
                  };
                })}
                filters={filters}
                onFilterChange={setFilters}
              />
            </li>
            <li>
              <DisplayDropdown
                selected={displayedFields}
                onChange={setDisplayedFields}
              />
            </li>
          </ul>
          <div>
            <div
              className="h-[50px] flex items-center mt-[12px] pl-3"
              style={{ fontFamily: "Liberation Serif" }}
            >
              <div className="flex whitespace-nowrap text-[#303133] font-bold">
                <span className="text-[13px] min-w-[35.5px] h-[28px] leading-[28px] align-top mr-2.5 font-normal text-gray-600">
                  Totally {users && users.length} Items
                </span>
                <div className="flex justify-center items-center">
                  {/* <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="w-[30px] h-[28px] bg-[#f4f4f5] rounded-md hover:cursor-pointer disabled:cursor-not-allowed mr-2 group pl-2"
                  >
                    <svg
                      className="w-3 fill-[#606266] group-disabled:fill-gray-300"
                      viewBox="0 0 55.753 55.753"
                    >
                      <path
                        d="M12.745 23.915c.283-.282.59-.52.913-.727L35.266 1.581a5.4 5.4 0 0 1 7.637 7.638L24.294 27.828l18.705 18.706a5.4 5.4 0 0 1-7.636 7.637L13.658 32.464a5.367 5.367 0 0 1-.913-.727 5.367 5.367 0 0 1-1.572-3.911 5.369 5.369 0 0 1 1.572-3.911z"
                        data-original="#000000"
                      />
                    </svg>
                  </button>

                  <div className="flex space-x-2">
                    {[...Array(totalPages).keys()].map((number) => (
                      <button
                        key={number}
                        onClick={() => handlePageChange(number + 1)}
                        className={`w-[30px] h-[28px] rounded-md text-sm font-medium ${
                          currentPage === number + 1
                            ? "bg-gray-500 text-white"
                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                      >
                        {number + 1}
                      </button>
                    ))}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="w-[30px] h-[28px] bg-[#f4f4f5] rounded-md hover:cursor-pointer disabled:cursor-not-allowed ml-3 items-center group pl-2"
                  >
                    <svg
                      className="w-3 fill-[#606266] group-disabled:fill-gray-300 rotate-180"
                      viewBox="0 0 55.753 55.753"
                    >
                      <path
                        d="M12.745 23.915c.283-.282.59-.52.913-.727L35.266 1.581a5.4 5.4 0 0 1 7.637 7.638L24.294 27.828l18.705 18.706a5.4 5.4 0 0 1-7.636 7.637L13.658 32.464a5.367 5.367 0 0 1-.913-.727 5.367 5.367 0 0 1-1.572-3.911 5.369 5.369 0 0 1 1.572-3.911z"
                        data-original="#000000"
                      />
                    </svg>
                  </button> */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full h-[calc(100vh-250px)] rounded-md mb-20  overflow-y-auto mt-2">
        <table className="w-full whitespace-nowrap mb-[100px]">
          <thead className="text-left sticky top-0 z-10 bg-gray-200 text-md font-semibold text-gray-700 ">
            <tr>
              <th
                className="p-4 cursor-pointer select-none"
                title="Sort by Name"
              >
                Name
              </th>
              <th
                className="p-4 cursor-pointer select-none"
                title="Sort by Name"
              >
                Access
              </th>
              {tableDisplay.main_location && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Name"
                >
                  Main Location
                </th>
              )}
              {tableDisplay.email && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Email"
                >
                  Email
                </th>
              )}
              {tableDisplay.mobile && (
                <th
                  className="p-4 cursor-pointer select-none"
                  title="Sort by Phone"
                >
                  Mobile
                </th>
              )}
              {tableDisplay.stress_profile && (
                <th className="p-4">Stress Profile</th>
              )}
              {tableDisplay.payrollId && <th className="p-4">Payroll ID</th>}
              {tableDisplay.leave_balance && (
                <th className="p-4">Leave Balance</th>
              )}
              {tableDisplay.connected_to && (
                <th className="p-4">Connected To</th>
              )}
              {tableDisplay.payrates && <th className="p-4">Payrates</th>}
              {tableDisplay.status && <th className="p-4">Allowed</th>}
              <th className="p-4 w-16 text-center">Action</th>
            </tr>
          </thead>
          <tbody className="border-b">
            {filteredPeoples.length === 0 ? (
              <tr>
                <td colSpan={10} className="p-4 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            ) : (
              filteredPeoples.map((user, index) => (
                <tr
                  key={user._id || index}
                  className="border-t text-sm text-gray-800 hover:bg-gray-50"
                >
                  <td className="p-4 flex h-32 items-center">
                    <UserAvatar user={user} />
                    <div>
                      <div className="font-medium text-gray-700">
                        {user.firstName} {user.lastName}
                      </div>
                    </div>
                  </td>

                  <td className="p-4">
                    {accessMap[user.deputyAccessLevel] || "employee"}
                  </td>
                  {tableDisplay.main_location && (
                    <td className="p-4">
                      {user?.locations?.primaryLocation?.name || ""}
                    </td>
                  )}
                  {tableDisplay.email && (
                    <td className="p-4 break-all text-gray-500">
                      {user.email || ""}
                    </td>
                  )}
                  {tableDisplay.mobile && (
                    <td className="p-4">{user.phone || "N/A"}</td>
                  )}
                  {/* <td className="p-4">{user.pronouns || "N/A"}</td>
                  <td className="p-4 capitalize">
                    {user.deputyAccessLevel || "employee"}
                  </td>
                  <td className="p-4">{getLocationName(user.locationID)}</td> */}
                  {tableDisplay.stress_profile && (
                    <td className="p-4">
                      {stressProfileMap[user?.stressProfile]}
                    </td>
                  )}
                  {tableDisplay.payrollId && (
                    <td className="p-4">{user?.payrollId}</td>
                  )}
                  {tableDisplay.leave_balance && (
                    <td className="p-4">{user?.leave_balance}</td>
                  )}
                  {tableDisplay.connected_to && (
                    <td className="p-4">{user?.connected_to}</td>
                  )}
                  {tableDisplay.payrates && (
                    <td className="p-4 relative ">
                      <div className="font-semibold bg-red-50 p-1 border border-red-100 rounded-sm w-fit text-red-600 mb-2">
                        {employmentTypes[user.employmentType]}
                      </div>
                      {renderPayRates(user.employmentType, user.payRates)}
                    </td>
                  )}
                  {tableDisplay.status && (
                    <td className="p-4">
                      <button
                        onClick={() =>
                          toggleUserAllowBlock(user._id, user.allowed)
                        }
                        className={`inline-flex items-center gap-1 font-medium p-2 rounded ${
                          user.allowed
                            ? "text-green-600 hover:bg-green-200"
                            : "text-red-600 hover:bg-red-200"
                        }`}
                        type="button"
                      >
                        {user.allowed ? (
                          <>
                            <i
                              className="fa fa-check-circle mr-2"
                              aria-hidden="true"
                            ></i>
                            Allowed
                          </>
                        ) : (
                          <>
                            <i
                              className="fa fa-times-circle mr-2"
                              aria-hidden="true"
                            ></i>
                            Blocked
                          </>
                        )}
                      </button>
                    </td>
                  )}
                  {/* <td className="p-4">
                    {(user.locationID && user.locationID.name) || ""}
                  </td>
                  <td className="p-4">
                    {(user.locationID && user.locationID.name) || ""}
                  </td> */}
                  {/* <td className="p-4">
                    <div className="flex justify-end">
                        <div>
                            <DropDownMenu options={actionOptions} onSelect={handleSelectAction} w1={40} w2={170}/>
                        </div>
                    </div>
                  </td> */}
                  <td className="p-4 text-center space-x-2">
                    <UserMenu
                      onView={() => showUserView(user._id)}
                      onEditPersonal={() => showPersonalInfoEdit(user._id)}
                      onEditEmployment={() => showEmploymentInfoEdit(user._id)}
                      onArchive={() => archiveUser(user._id)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* <Pagination /> */}

      <EmploymentDetails
        id={selectedUserID}
        open={openEmployment}
        onClose={closeEmploymentDetails}
        locations={locations}
      />

      <PersonalDetailsForm
        id={selectedUserID}
        open={openPersonal}
        locations={locations}
        onClose={closePersonalDetails}
      />

      {openView && (
        <UserProfileDialog
          onClose={() => setOpenView(false)}
          userID={selectedUserID}
          locations={locations}
          open={openView}
        />
      )}

      {modify && (
        <UserProfileDialog
          id={selectedUserID}
          onClose={() => setModify(false)}
          locations={locations}
        />
      )}
      {openAddUserModal && (
        <UserAddDialog onClose={() => AddModalClose()} locations={locations} />
      )}
    </div>
  );
};

export default People;

function renderPayRates(employmentType, payRates) {
  // If payRates is missing, show nothing
  if (!payRates) return null;

  // Fixed Rate
  if (payRates.payRateType === "fixed_rate") {
    return (
      <div>
        <div>
          <span className="font-medium">Fixed Rate:</span> €{payRates.fixedRate}
        </div>
      </div>
    );
  }

  // Salary
  if (payRates.payRateType === "salary") {
    return (
      <div>
        <div>
          <span className="font-medium">Salary:</span> €{payRates.salaryAmount}{" "}
          ({payRates.salaryPeriod})
        </div>
      </div>
    );
  }

  // Hourly or similar
  return (
    <div>
      <div>
        <span className="font-medium">Weekday:</span> €{payRates.weekday}
      </div>
      <div>
        <span className="font-medium">Saturday:</span> €{payRates.saturday}
      </div>
      <div>
        <span className="font-medium">Sunday:</span> €{payRates.sunday}
      </div>
      <div>
        <span className="font-medium">Public Holiday:</span> €
        {payRates.publicHoliday}
      </div>
    </div>
  );
}
